import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { getBulkDiscountsForVariantOption, getTieredOptionLine } from '@repo/lib';
import { formatPrice, formatPriceInput } from '../../../services/formatPrice';

import clsx from 'clsx';
import {
  CheckIcon,
  ChevronDownIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Badge from '../../Badge';
import useCheckoutState, { checkoutLayouts } from '../../../hooks/useCheckoutState';
import useCheckoutFormHelpers from '../../../hooks/useCheckoutFormHelpers';
import useAiTarget from '../../../hooks/useAiTarget';
import getSubscriptionIntervalWithSlash from '../../../services/translation/getSubscriptionIntervalWithSlash';
import QuantityButton from './QuantityButton';
import QuantityField from './QuantityField';
import useCartCost from '../../../hooks/useCartCost';
import checkIsVariantVisible from '../../../lib/checkIsVariantVisible';
import checkIsVariantEnabled from '../../../lib/checkIsVariantEnabled';
import checkIsVariantActive from '../../../lib/checkIsVariantActive';
import { getImageSrcAndSrcSet } from '../../../services/getUrlFromCdn';
import MultilineText from '../../MultilineText';
import StyledElement from '@repo/checkout/components/StyledElement';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import { TextInput } from '../../Form';
import isPwywOption from '../../../lib/isPwywOption';
import checkIsVariantForPrice from '../../../lib/checkIsVariantForPrice';
import shouldShowVariantOptionInterval from '../../../lib/shouldShowVariantOptionInterval';
import shouldShowVariantPlanIterations from '../../../lib/shouldShowVariantPlanIterations';
import useSelectedPrice from '../../../hooks/useSelectedPrice';
import { getEffectiveInterval } from '../../../lib/multiPrice';
import getVariantPwywSuffix from '../../../lib/getVariantPwywSuffix';
import resolveInheritedEnabled from '../../../lib/resolveInheritedEnabled';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';

const variantSoldOutClasses = clsx(
  'text-[var(--cp_checkout-statusBadges-soldOut-color)]',
  'bg-[var(--cp_checkout-statusBadges-soldOut-background-color)]',
  '[font-family:var(--cp_checkout-statusBadges-font-family)]',
  '[font-style:var(--cp_checkout-statusBadges-font-style)]',
  '[font-size:var(--cp_checkout-statusBadges-font-size)]',
  '[line-height:var(--cp_checkout-statusBadges-line-height)]',
  '[font-weight:var(--cp_checkout-statusBadges-font-weight)]',
  'whitespace-nowrap',
);

function getVariantOptionLine(variantOption, unitPrice, quantity, bulkDiscounts) {
  return getTieredOptionLine({
    unitPrice,
    quantity,
    bulkDiscounts: getBulkDiscountsForVariantOption(bulkDiscounts, variantOption._id),
  });
}

function isSoldOut(variant, option) {
  if (!variant.manageVariantOptionStock) return false;

  if (typeof option.stock !== 'number') return false;

  return Number(option.stock) <= 0;
}

/**
 * The recurring suffix on a variant option price ("+$5 / month"). When a
 * RECURRING option renders without a resolvable cadence — the selected price
 * is one-time (the preview's dimmed out-of-scope state) or no price is chosen
 * yet — fall back to the generic "/ recurring": the option still bills on a
 * schedule, and dropping the suffix entirely reads as a bug (1188 follow-up).
 * One-time options never get a suffix. Exported for unit testing.
 */
export function VariantOptionIntervalSuffix({ option, showInterval, effectiveInterval, labels }) {
  if (showInterval) {
    return (
      <span className="cp_variant-price-interval">
        {' '}
        {getSubscriptionIntervalWithSlash(
          effectiveInterval.interval,
          effectiveInterval.intervalCount,
          labels,
        )}
      </span>
    );
  }
  if (option?.type !== 'recurring') return null;
  return <span className="cp_variant-price-interval"> / {labels.recurringText}</span>;
}

const Variants = () => {
  const {
    isPreview,
    checkoutLayout,
    product,
    form,
    query,
    currencyLocale,
    effectiveSelectedPriceId,
  } = useCheckoutState();
  const aiTarget = useAiTarget();

  const { variants: productVariants } = product;

  // filter out disabled variants (including groups whose every option is
  // disabled) and variants not scoped to the selected price
  const variants = productVariants.filter(
    variant =>
      checkIsVariantActive(variant) &&
      (isPreview || checkIsVariantForPrice(variant, effectiveSelectedPriceId)),
  );

  if (!variants?.length) return '';

  if (variants.length === 1 && !variants[0].layout?.collapseVariantOptions) {
    const variant = variants[0];

    const enabled = checkIsVariantEnabled(variant, form.variants, variants);
    const visible = checkIsVariantVisible(variant, query);
    const inPriceScope = checkIsVariantForPrice(variant, effectiveSelectedPriceId);

    if ((!enabled || !visible) && !isPreview) return '';

    return (
      <div className="cp_variants" {...aiTarget('variants')}>
        <div
          className={clsx(`cp_variant-wrapper cp_variant-${variant.reference}-wrapper`, 'px-4', {
            'opacity-25': (!enabled || !visible || !inPriceScope) && isPreview,
          })}
        >
          {/* TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed */}
          <Variant variant={variant} currency={product.currency} locale={currencyLocale} />
        </div>
      </div>
    );
  }

  const renderedIndices = variants.reduce((acc, variant, index) => {
    const enabled = checkIsVariantEnabled(variant, form.variants, variants);
    const visible = checkIsVariantVisible(variant, query);
    if ((enabled && visible) || isPreview) acc.push(index);
    return acc;
  }, []);

  return (
    <div
      {...aiTarget('variants')}
      className={clsx('cp_variants', {
        'mx-4':
          checkoutLayout === checkoutLayouts.SPLIT || checkoutLayout === checkoutLayouts.DOUBLE,
      })}
    >
      {variants.map((variant, index) => {
        const enabled = checkIsVariantEnabled(variant, form.variants, variants);

        const visible = checkIsVariantVisible(variant, query);

        const inPriceScope = checkIsVariantForPrice(variant, effectiveSelectedPriceId);

        if ((!enabled || !visible) && !isPreview) return '';

        // not accordion
        if (!variant.layout?.collapseVariantOptions) {
          return (
            <>
              <div
                key={variant._id}
                className={clsx(
                  `cp_variant-wrapper cp_variant-${variant.reference}-wrapper`,
                  'first:pt-0 pt-4',
                  {
                    'opacity-25': (!enabled || !visible || !inPriceScope) && isPreview,
                    'px-4': checkoutLayout === checkoutLayouts.CENTER,
                  },
                )}
              >
                {/* TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed */}
                <Variant variant={variant} currency={product.currency} locale={currencyLocale} />
              </div>
            </>
          );
        }

        const variantOptions = variant.options.filter(option => option.status === 'enabled');

        const selectedOptions = variantOptions
          .filter(option => {
            const formVariant = form.variants[variant._id];

            if (!formVariant) return false;

            const selectedOption = formVariant.find(v => v.optionId === option._id);

            if (!selectedOption) return false;

            return selectedOption.quantity > 0;
          })
          .map(option => {
            const formVariant = form.variants[variant._id];

            if (!formVariant) return false;

            const selectedOption = formVariant.find(v => v.optionId === option._id);

            if (!selectedOption) return false;

            return {
              ...option,
              quantity: selectedOption.quantity,
            };
          });

        // accordion
        return (
          <React.Fragment key={variant._id}>
            {index === renderedIndices[0] && (
              <StyledElement
                elementPath="checkout.variants.variantOptions.divider"
                className="cp_variant-divider border-b border-[var(--cp_checkout-divider-border-color)]"
              />
            )}
            <Disclosure
              as="div"
              className={clsx(
                'cp_variant-accordion-wrapper',
                'last:-mb-4', // collapse margin on the last accordion wrapper
                {
                  'opacity-25': (!enabled || !visible || !inPriceScope) && isPreview,
                  'px-4': checkoutLayout === checkoutLayouts.CENTER,
                },
              )}
            >
              {({ open, close }) => (
                <>
                  <DisclosureButton className={clsx('cp_variant-accordion', 'py-2 my-2 w-full')}>
                    <div className="cp_variant-accordion-label-wrapper flex justify-between items-center">
                      <StyledElement
                        elementPath="checkout.variants.name"
                        as="label"
                        className={clsx(
                          'cp_variant-accordion-label',
                          'text-[var(--cp_checkout-variants-name-color)]',
                          '[font-family:var(--cp_checkout-variants-name-font-family)]',
                          '[font-style:var(--cp_checkout-variants-name-font-style)]',
                          '[font-size:var(--cp_checkout-variants-name-font-size)]',
                          '[line-height:var(--cp_checkout-variants-name-line-height)]',
                          '[font-weight:var(--cp_checkout-variants-name-font-weight)]',
                          variant.required && 'after:content-["*"] after:ml-0.5',
                        )}
                        style={{ marginBottom: 0, cursor: 'inherit' }}
                      >
                        {variant.name}
                      </StyledElement>
                      <ChevronDownIcon className="cp_variant-accordion-label-icon h-5 w-5" />
                    </div>

                    {/* selected variant option in closed accordion */}
                    {selectedOptions &&
                      selectedOptions.length > 0 &&
                      !open &&
                      selectedOptions.map(selectedOption => (
                        <div
                          className="cp_variant-accordion-selected-option text-left mt-2 flex"
                          key={selectedOption._id}
                        >
                          {variant.selectionType === 'quantity' && (
                            <>
                              <div className="cp_variant-accordion-selected-option-quantity-wrapper mr-1 whitespace-nowrap">
                                {selectedOption.quantity}
                                <XMarkIcon className="cp_variant-accordion-selected-option-quantity-icon w-3 h-3 mt-0.5 inline-block" />
                              </div>

                              <SelectedAccordionVariantOptionLabel
                                option={selectedOption}
                                showName={variant.layout?.showVariantOptionNames}
                                showPrice={variant.layout?.showVariantOptionPrices}
                                showPriceSign={variant.layout?.showVariantOptionPriceSign}
                                textAlign={variant.layout?.textAlign}
                                imageSize={variant.layout?.imageSize}
                                // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                                currency={product.currency}
                                locale={currencyLocale}
                                isSoldOut={isSoldOut(variant, selectedOption)}
                              />
                            </>
                          )}

                          {variant.selectionType !== 'quantity' && (
                            <>
                              <CheckIcon className="cp_variant-accordion-selected-option-icon w-5 h-5 mr-1 mt-0.5 text-[var(--cp_color-secondary)]" />

                              <SelectedAccordionVariantOptionLabel
                                option={selectedOption}
                                showName={variant.layout?.showVariantOptionNames}
                                showPrice={variant.layout?.showVariantOptionPrices}
                                showPriceSign={variant.layout?.showVariantOptionPriceSign}
                                textAlign={variant.layout?.textAlign}
                                imageSize={variant.layout?.imageSize}
                                // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                                currency={product.currency}
                                locale={currencyLocale}
                                isSoldOut={isSoldOut(variant, selectedOption)}
                              />
                            </>
                          )}
                        </div>
                      ))}
                  </DisclosureButton>

                  <DisclosurePanel
                    className={clsx(
                      `cp_variant-wrapper cp_variant-${variant.reference}-wrapper`,
                      'last:pb-4', // add padding to the last variant wrapper
                    )}
                  >
                    <Variant
                      variant={variant}
                      onSelectVariantOption={close}
                      // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                      currency={product.currency}
                      locale={currencyLocale}
                    />
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>

            {index !== renderedIndices[renderedIndices.length - 1] && (
              <StyledElement
                elementPath="checkout.variants.variantOptions.divider"
                className="cp_variant-divider border-b border-[var(--cp_checkout-divider-border-color)]"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Variant = ({ variant, onSelectVariantOption, currency, locale: currencyLocale }) => {
  const { form } = useCheckoutState();
  const { variants: formVariants } = form;

  const { setVariantOption, unsetVariantOption } = useCheckoutFormHelpers();

  const variantOptions = variant.options.filter(option => option.status === 'enabled');

  // if there is quantity selection, don't use radio buttons, use divs but keep the same styles
  if (variant.selectionType === 'quantity') {
    return (
      <div className={clsx(`cp_variant cp_variant-${variant.reference}`)}>
        {variant.layout?.showVariantName && !variant.layout?.collapseVariantOptions && (
          <StyledElement
            elementPath="checkout.variants.name"
            as="span"
            className={clsx(
              'cp_variant-label',
              'block',
              'text-[var(--cp_checkout-variants-name-color)]',
              '[font-family:var(--cp_checkout-variants-name-font-family)]',
              '[font-style:var(--cp_checkout-variants-name-font-style)]',
              '[font-size:var(--cp_checkout-variants-name-font-size)]',
              '[line-height:var(--cp_checkout-variants-name-line-height)]',
              '[font-weight:var(--cp_checkout-variants-name-font-weight)]',
              '[padding-bottom:var(--cp_checkout-variants-name-padding-bottom)]',
              variant.required && 'after:content-["*"] after:ml-0.5',
            )}
          >
            {variant.name}
          </StyledElement>
        )}

        <div
          className={clsx(`cp_variant-items`, {
            'grid gap-2': variant.layout?.variantOptionLayout === 'grid',
            '-space-y-px': variant.layout?.variantOptionLayout !== 'grid',
          })}
          style={{
            ...(variant.layout?.variantOptionLayout === 'grid' && {
              gridTemplateColumns: `repeat(${variant.layout?.variantOptionColumns}, minmax(0, 1fr))`,
            }),
            ...(variant.layout?.variantOptionLayout !== 'grid' && {
              '--variant-negative-spacing':
                'calc(-1 * var(--cp_checkout-variants-variantOptions-border-width))',
            }),
          }}
        >
          {variantOptions.map((option, index) => {
            const value = formVariants[variant._id]?.find(v => v.optionId === option._id);

            if (!value) return null;

            function setQuantity(quantity) {
              setVariantOption(variant._id, option._id, quantity);
            }

            const selectedOption = {
              ...option,
              quantity: value.quantity,
            };

            return (
              <div
                className={clsx(`cp_variant-item block w-full`)}
                key={option._id}
                style={{
                  ...(variant.layout?.variantOptionLayout !== 'grid' &&
                    index > 0 && {
                      marginTop: 'var(--variant-negative-spacing)',
                    }),
                }}
              >
                <div
                  className={clsx(
                    'cp_variant-option relative flex h-full',
                    '[border-width:var(--cp_checkout-variants-variantOptions-border-width)] border-solid',
                    'bg-[var(--cp_checkout-variants-variantOptions-background-color)] border-[var(--cp_checkout-variants-variantOptions-border-color)]',
                    {
                      'p-2': variant.layout?.spacing === 'compact',
                      'p-4': variant.layout?.spacing === 'comfortable' || !variant.layout?.spacing,
                      'px-2':
                        variant.layout?.variantOptionLayout === 'grid' &&
                        variant.layout?.variantOptionColumns > 3,
                      'rounded-[var(--cp_checkout-variants-variantOptions-border-radius)]':
                        variant.layout?.variantOptionLayout === 'grid',
                      'rounded-tl-[var(--cp_checkout-variants-variantOptions-border-radius)] rounded-tr-[var(--cp_checkout-variants-variantOptions-border-radius)]':
                        index === 0,
                      'rounded-bl-[var(--cp_checkout-variants-variantOptions-border-radius)] rounded-br-[var(--cp_checkout-variants-variantOptions-border-radius)]':
                        index === variantOptions.length - 1,
                    },
                  )}
                >
                  <div className="cp_variant-option-label-wrapper w-full space-y-2">
                    <VariantOptionLabel
                      variant={variant}
                      setQuantity={setQuantity}
                      option={selectedOption}
                      currency={currency}
                      locale={currencyLocale}
                      isSoldOut={isSoldOut(variant, option)}
                      showName={variant.layout?.showVariantOptionNames}
                      showPrice={variant.layout?.showVariantOptionPrices}
                      showPriceSign={variant.layout?.showVariantOptionPriceSign}
                      textAlign={variant.layout?.textAlign}
                      imageSize={variant.layout?.imageSize}
                      variantId={variant._id}
                      isSelected={value.quantity > 0}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(`cp_variant cp_variant-${variant.reference}`)}>
      {variant.layout?.showVariantName && !variant.layout?.collapseVariantOptions && (
        <StyledElement
          elementPath="checkout.variants.name"
          as="span"
          className={clsx(
            'cp_variant-label',
            'block',
            'bg-[var(--cp_checkout-variants-name-background-color)]',
            'text-[var(--cp_checkout-variants-name-color)]',
            '[font-family:var(--cp_checkout-variants-name-font-family)]',
            '[font-style:var(--cp_checkout-variants-name-font-style)]',
            '[font-size:var(--cp_checkout-variants-name-font-size)]',
            '[line-height:var(--cp_checkout-variants-name-line-height)]',
            '[font-weight:var(--cp_checkout-variants-name-font-weight)]',
            '[padding-bottom:var(--cp_checkout-variants-name-padding-bottom)]',
            variant.required && 'after:content-["*"] after:ml-0.5',
          )}
        >
          {variant.name}
        </StyledElement>
      )}

      <div
        className={clsx(`cp_variant-items`, {
          'grid gap-2': variant.layout?.variantOptionLayout === 'grid',
          '-space-y-px': variant.layout?.variantOptionLayout !== 'grid',
        })}
        style={{
          ...(variant.layout?.variantOptionLayout === 'grid' && {
            gridTemplateColumns: `repeat(${variant.layout?.variantOptionColumns}, minmax(0, 1fr))`,
          }),
          ...(variant.layout?.variantOptionLayout !== 'grid' && {
            '--variant-negative-spacing':
              'calc(-1 * var(--cp_checkout-variants-variantOptions-border-width))',
          }),
        }}
      >
        {variantOptions.map((option, index) => {
          const formVariant = formVariants[variant._id];
          const formOption = formVariant?.find(v => v.optionId === option._id);
          const isSelected = formOption?.quantity > 0;

          function onOptionClick(e, optionId) {
            // Prevent the button's click handler if an <a> was clicked inside
            if (e.target.closest('a')) {
              return;
            }

            const clickedOption = variantOptions.find(o => o._id === optionId);
            const clickedIsPwyw = isPwywOption(clickedOption);

            // if clicked on the currently selected option
            if (isSelected) {
              if (variant.selectionType === 'multiple') {
                unsetVariantOption(variant._id, optionId);
              } else {
                // if variant is required
                // set the same variant option so that accordion still closes
                if (variant.required) {
                  setVariantOption(variant._id, optionId, 1);
                } else {
                  // if variant is not required, unset the selected variant option
                  unsetVariantOption(variant._id, optionId);
                }
              }
            } else {
              if (variant.selectionType === 'multiple') {
                // if multiple select is allowed, set the quantity to 1
                // leave the quantity of other variant options in the same variant as is
                setVariantOption(variant._id, optionId, 1);
              } else {
                // if multiple select is not allowed, set the quantity to 1 and set the quantity of all other variant options in the same variant to 0
                formVariants?.[variant._id]?.forEach(formVariantOption => {
                  if (formVariantOption.optionId !== optionId) {
                    unsetVariantOption(variant._id, formVariantOption.optionId);
                  }
                });

                setVariantOption(variant._id, optionId, 1);

                if (onSelectVariantOption && typeof onSelectVariantOption === 'function') {
                  onSelectVariantOption();
                }
              }
            }

            if (clickedIsPwyw && !isSelected) {
              setTimeout(() => {
                const inputEl = document.getElementById(`pwyw-${variant._id}-${optionId}`);
                if (inputEl) inputEl.focus();
              }, 0);
            }
          }

          const selectedOption = {
            ...option,
            quantity: formOption?.quantity,
          };

          return (
            <button
              onClick={e => onOptionClick(e, option._id)}
              type="button"
              className={clsx(`cp_variant-item block w-full`, {
                'cursor-not-allowed': isSoldOut(variant, option),
              })}
              key={option._id}
              disabled={isSoldOut(variant, option)}
              style={{
                ...(variant.layout?.variantOptionLayout !== 'grid' &&
                  index > 0 && {
                    marginTop: 'var(--variant-negative-spacing)',
                  }),
              }}
            >
              <div
                className={clsx(
                  'cp_variant-option relative flex h-full',
                  '[border-width:var(--cp_checkout-variants-variantOptions-border-width)] border-solid',
                  {
                    'p-2': variant.layout?.spacing === 'compact',
                    'p-4': variant.layout?.spacing === 'comfortable' || !variant.layout?.spacing,
                    'px-2':
                      variant.layout?.variantOptionLayout === 'grid' &&
                      variant.layout?.variantOptionColumns > 3,
                    'rounded-[var(--cp_checkout-variants-variantOptions-border-radius)]':
                      variant.layout?.variantOptionLayout === 'grid',
                    'rounded-tl-[var(--cp_checkout-variants-variantOptions-border-radius)] rounded-tr-[var(--cp_checkout-variants-variantOptions-border-radius)]':
                      index === 0,
                    'rounded-bl-[var(--cp_checkout-variants-variantOptions-border-radius)] rounded-br-[var(--cp_checkout-variants-variantOptions-border-radius)]':
                      index === variantOptions.length - 1,
                    'cp_variant-option--selected z-10': isSelected,
                    'bg-[var(--cp_checkout-variants-variantOptions-background-color)]': !isSelected,
                    'bg-[var(--cp_checkout-variants-variantOptions-background-color-selected)]':
                      isSelected,
                    'border-[var(--cp_checkout-variants-variantOptions-border-color)]': !isSelected,
                    'border-[var(--cp_checkout-variants-variantOptions-border-color-selected)]':
                      isSelected,
                    'ring-1 ring-[var(--cp_checkout-variants-variantOptions-border-color-selected)]':
                      isSelected,
                  },
                )}
              >
                <div className="flex justify-between w-full">
                  <VariantOptionLabel
                    variant={variant}
                    option={selectedOption}
                    currency={currency}
                    locale={currencyLocale}
                    isSoldOut={isSoldOut(variant, option)}
                    showName={variant.layout?.showVariantOptionNames}
                    showPrice={variant.layout?.showVariantOptionPrices}
                    showPriceSign={variant.layout?.showVariantOptionPriceSign}
                    textAlign={variant.layout?.textAlign}
                    imageSize={variant.layout?.imageSize}
                    variantId={variant._id}
                    isSelected={isSelected}
                    onPwywSelect={optionId => {
                      if (variant.selectionType !== 'multiple') {
                        formVariants?.[variant._id]?.forEach(fv => {
                          if (fv.optionId !== optionId && fv.quantity > 0) {
                            unsetVariantOption(variant._id, fv.optionId);
                          }
                        });
                      }
                      setVariantOption(variant._id, optionId, 1);
                    }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

function VariantOptionQuantitySelect({ textAlign, option, selectedQuantity, setQuantity }) {
  return (
    <div
      className={clsx('cp_variant-quantity-wrapper flex items-stretch', {
        'justify-center': textAlign === 'center',
      })}
    >
      <QuantityButton
        className="cp_variant-quantity-button-minus"
        onClick={event => {
          event.stopPropagation();

          if (selectedQuantity <= 0) {
            return;
          }

          setQuantity(Number(selectedQuantity - 1));
        }}
      >
        <MinusIcon className="cp_variant-quantity-button-icon cp_variant-quantity-button-minus-icon w-5 h-5" />
      </QuantityButton>
      <QuantityField
        className="cp_variant-quantity-input"
        id={option.optionId}
        name={option.optionId}
        value={selectedQuantity}
        onChange={setQuantity}
        required={false}
        min={0}
        max={9999}
      />
      <QuantityButton
        className="cp_variant-quantity-button-plus"
        type="button"
        onClick={event => {
          event.stopPropagation();

          setQuantity(Number(selectedQuantity + 1));
        }}
      >
        <PlusIcon className="cp_variant-quantity-button-icon cp_variant-quantity-button-plus-icon w-5 h-5" />
      </QuantityButton>
    </div>
  );
}

function SelectedAccordionVariantOptionLabel({
  option,
  currency,
  locale: currencyLocale,
  isSoldOut,
  showName,
  showPrice,
  showPriceSign,
  textAlign = 'left',
}) {
  const { product, isPreview, theme } = useCheckoutState();
  const selectedPrice = useSelectedPrice();
  const labels = getThemeLabels(theme);
  const { planIterations } = useCartCost();

  // For multi-price products, resolve the selected price's interval (covers
  // single-price and default-selected products via useSelectedPrice).
  const accordionEffectiveInterval = useMemo(
    () => getEffectiveInterval(selectedPrice, product),
    [selectedPrice, product],
  );

  // See shouldShowVariantOptionInterval: only a recurring option carries the
  // interval; on multi-price it follows the selected price's cadence.
  const showInterval = shouldShowVariantOptionInterval({
    product,
    option,
    effectiveInterval: accordionEffectiveInterval,
  });

  const variantOptionPrice = option.additionalChargeAmount;
  const quantity = option.quantity ?? 1;
  const { lineAmount: variantOptionTotal } = getVariantOptionLine(
    option,
    variantOptionPrice,
    quantity,
    product.discounts,
  );
  const showVariantOptionTotal = showPrice && variantOptionPrice > 0 && variantOptionTotal >= 0;
  const image = getImageSrcAndSrcSetHelper({ url: option.image?.url, isPreview });

  return (
    <div className="cp_variant-option-label-wrapper flex flex-wrap w-full gap-2">
      <div
        className={clsx('cp_variant-option-label-container flex flex-nowrap w-full gap-x-4', {
          'text-left': textAlign === 'left',
        })}
      >
        {image && (
          <div
            className={clsx(
              'cp_variant-image-wrapper w-12 h-12 rounded overflow-hidden flex-shrink-0',
            )}
          >
            <img
              src={image.src}
              srcSet={image.srcSet}
              className="cp_variant-image object-cover h-full w-full"
            />
          </div>
        )}

        <div className="cp_variant-text-wrapper gap-x-1.5 flex flex-col">
          {showName && (
            <StyledElement
              elementPath="checkout.variants.variantOptions.name"
              as="div"
              className={clsx(
                'cp_variant-name overflow-hidden flex-shrink-0',
                'text-[var(--cp_checkout-variants-variantOptions-name-color)]',
                '[font-family:var(--cp_checkout-variants-variantOptions-name-font-family)]',
                '[font-style:var(--cp_checkout-variants-variantOptions-name-font-style)]',
                '[font-size:var(--cp_checkout-variants-variantOptions-name-font-size)]',
                '[line-height:var(--cp_checkout-variants-variantOptions-name-line-height)]',
                '[font-weight:var(--cp_checkout-variants-variantOptions-name-font-weight)]',
              )}
            >
              <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
            </StyledElement>
          )}
        </div>

        <div className="ml-auto">
          {(showVariantOptionTotal || isSoldOut) && (
            <div
              className={clsx('cp_variant-price-wrapper gap-x-1.5 flex flex-col', {
                'text-right': showName && textAlign === 'left',
                'flex-1': !showName,
              })}
            >
              {showVariantOptionTotal && (
                <>
                  <div
                    className={clsx('cp_variant-price space-y-1 w-full', {
                      'text-right': textAlign === 'left',
                    })}
                  >
                    {showPriceSign && variantOptionTotal > 0 && (
                      <span className="cp_variant-price-plus-sign text-[var(--cp_checkout-variants-variantOptions-plusSign-color)]">
                        +
                      </span>
                    )}

                    <span className="cp_variant-price-amount">
                      {formatPrice({
                        amount: variantOptionTotal,
                        currency: currency,
                        locale: currencyLocale,
                        style: 'currency',
                      })}
                    </span>

                    <VariantOptionIntervalSuffix
                      option={option}
                      showInterval={showInterval}
                      effectiveInterval={accordionEffectiveInterval}
                      labels={labels}
                    />
                  </div>

                  {shouldShowVariantPlanIterations({
                    selectedPrice,
                    product,
                    option,
                    planIterations,
                  }) && <PlanIterations planIterations={planIterations} />}
                </>
              )}

              {isSoldOut && (
                <Badge
                  className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses, {
                    'mr-auto': textAlign === 'left',
                  })}
                >
                  {labels.soldOutText}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VariantOptionLabel({
  variant,
  setQuantity = () => {},
  option,
  currency,
  locale,
  isSoldOut,
  showName,
  showPrice,
  showPriceSign,
  textAlign = 'left',
  imageSize = 'small',
  variantId,
  isSelected,
  onPwywSelect = () => {},
}) {
  const { product, isPreview, theme, form } = useCheckoutState();
  const selectedPrice = useSelectedPrice();
  const { planIterations } = useCartCost();
  const labels = getThemeLabels(theme);

  // For multi-price products, resolve the selected price's interval for
  // variant option price display (e.g. "+$5/mo").
  const variantEffectiveInterval = useMemo(
    () => getEffectiveInterval(selectedPrice, product),
    [selectedPrice, product],
  );

  // Show the recurring interval ("/month") only on a RECURRING option — a
  // one-time add-on is charged once even when a recurring price is selected.
  // Multi-price products take the cadence from the selected price; legacy
  // single-price products additionally require a subscription product.
  const showInterval = shouldShowVariantOptionInterval({
    product,
    option,
    effectiveInterval: variantEffectiveInterval,
  });

  const isPwyw = isPwywOption(option);
  const variantOptionPrice = option.additionalChargeAmount;
  const quantity = option.quantity ?? 1;

  const pwywFormOption = isPwyw
    ? form.variants[variantId]?.find(v => v.optionId === option._id)
    : null;
  const pwywEnteredAmount = pwywFormOption?.payWhatYouWantAmount
    ? Number(formatPriceInput(pwywFormOption.payWhatYouWantAmount, currency))
    : 0;

  const effectivePrice =
    isPwyw && pwywEnteredAmount > 0 ? pwywEnteredAmount : option.additionalChargeAmount || 0;
  const optionLine = getVariantOptionLine(option, effectivePrice, quantity, product.discounts);
  const variantOptionTotal = optionLine.lineAmount;

  const currentVariantOptionPrice = variantOptionTotal ? optionLine.unitPrice : variantOptionPrice;

  // if the option has a discountedFromPrice, use it
  // otherwise, if the current variant option price is less than the variant option price, use the variant option price as the discounted from price
  // this would happen when there are bulk discounts applied to the variant option
  const discountedFromPrice = option.discountedFromPrice
    ? option.discountedFromPrice
    : currentVariantOptionPrice < variantOptionPrice
      ? variantOptionPrice
      : '';

  // Don't show regular price for PWYW options - the PWYW input handles the amount
  const showVariantOptionPrice =
    !isPwyw && showPrice && (variantOptionPrice === 0 || variantOptionPrice > 0);
  const showVariantOptionTotal =
    !isPwyw && quantity > 0 && variantOptionPrice > 0 && variantOptionTotal >= 0;

  const pwywQuantityTotal = isPwyw ? variantOptionTotal : 0;
  const pwywMinThresholdMet = pwywEnteredAmount >= option.additionalChargeAmount;
  const showPwywQuantityTotal =
    isPwyw &&
    pwywMinThresholdMet &&
    quantity > 0 &&
    pwywEnteredAmount > 0 &&
    pwywQuantityTotal >= 0;

  const image = getImageSrcAndSrcSetHelper({ url: option.image?.url, imageSize, isPreview });

  return (
    <div className="cp_variant-option-label flex flex-wrap w-full gap-2">
      {textAlign === 'left' && (
        <div
          className={clsx('cp_variant-option-left flex flex-nowrap w-full gap-x-4', {
            'text-left': textAlign === 'left',
            'flex-col gap-y-2': variant.layout?.variantOptionLayout === 'grid',
          })}
        >
          {image && (
            <div
              className={clsx('cp_variant-image-wrapper rounded overflow-hidden flex-shrink-0', {
                'w-16 h-16': imageSize === 'small',
                'w-32 h-32': imageSize === 'medium',
                'w-64 h-64': imageSize === 'large',
              })}
            >
              <img
                src={image.src}
                srcSet={image.srcSet}
                className="cp_variant-image object-cover h-full w-full"
              />
            </div>
          )}

          <div className="cp_variant-text-wrapper gap-x-1.5 flex flex-col">
            {showName && (
              <StyledElement
                elementPath="checkout.variants.variantOptions.name"
                as="div"
                className={clsx(
                  'cp_variant-name overflow-hidden flex-shrink-0',
                  'text-[var(--cp_checkout-variants-variantOptions-name-color)]',
                  '[font-family:var(--cp_checkout-variants-variantOptions-name-font-family)]',
                  '[font-style:var(--cp_checkout-variants-variantOptions-name-font-style)]',
                  '[font-size:var(--cp_checkout-variants-variantOptions-name-font-size)]',
                  '[line-height:var(--cp_checkout-variants-variantOptions-name-line-height)]',
                  '[font-weight:var(--cp_checkout-variants-variantOptions-name-font-weight)]',
                )}
              >
                <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
              </StyledElement>
            )}
            {!isPwyw && ((showPrice && variantOptionPrice) || isSoldOut) && (
              <div
                className={clsx('cp_variant-price-wrapper gap-x-1.5 flex flex-col', {
                  'flex-1': !showName,
                })}
              >
                {showPrice && variantOptionPrice > 0 && showVariantOptionPrice && (
                  <>
                    <StyledElement
                      elementPath="checkout.variants.variantOptions.price"
                      as="div"
                      className={clsx(
                        'cp_variant-price space-y-1 w-full',
                        'text-[var(--cp_checkout-variants-variantOptions-price-color)]',
                        '[font-family:var(--cp_checkout-variants-variantOptions-price-font-family)]',
                        '[font-style:var(--cp_checkout-variants-variantOptions-price-font-style)]',
                        '[font-size:var(--cp_checkout-variants-variantOptions-price-font-size)]',
                        '[line-height:var(--cp_checkout-variants-variantOptions-price-line-height)]',
                        '[font-weight:var(--cp_checkout-variants-variantOptions-price-font-weight)]',
                      )}
                    >
                      {discountedFromPrice && (
                        <StyledElement
                          elementPath="checkout.variants.variantOptions.discountedFromPrice"
                          as="span"
                          className={clsx(
                            'cp_variant-discounted-from line-through mr-1.5',
                            'text-[var(--cp_checkout-variants-variantOptions-discountedFromPrice-color)]',
                            '[font-family:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-family)]',
                            '[font-style:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-style)]',
                            '[font-size:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-size)]',
                            '[line-height:var(--cp_checkout-variants-variantOptions-discountedFromPrice-line-height)]',
                            '[font-weight:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-weight)]',
                          )}
                        >
                          {formatPrice({
                            amount: discountedFromPrice,
                            currency: currency,
                            locale: locale,
                            style: 'currency',
                          })}
                        </StyledElement>
                      )}

                      {showPriceSign && variantOptionPrice > 0 && (
                        <span className="cp_variant-price-plus-sign text-[var(--cp_checkout-variants-variantOptions-plusSign-color)]">
                          +
                        </span>
                      )}

                      <span className="cp_variant-price-amount">
                        {formatPrice({
                          amount: currentVariantOptionPrice || variantOptionPrice,
                          currency: currency,
                          locale: locale,
                          style: 'currency',
                        })}
                      </span>

                      <VariantOptionIntervalSuffix
                        option={option}
                        showInterval={showInterval}
                        effectiveInterval={variantEffectiveInterval}
                        labels={labels}
                      />
                    </StyledElement>

                    {shouldShowVariantPlanIterations({
                      selectedPrice,
                      product,
                      option,
                      planIterations,
                    }) && <PlanIterations planIterations={planIterations} />}
                  </>
                )}
                {isSoldOut && (
                  <div
                    className={clsx('cp_variant-price-wrapper gap-x-1.5 flex flex-col', {
                      'flex-1': !showName,
                    })}
                  >
                    <Badge
                      className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses, {
                        'mr-auto': textAlign === 'left',
                      })}
                    >
                      {labels.soldOutText}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {isPwyw && isSoldOut && (
              <Badge
                className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses, {
                  'mr-auto': textAlign === 'left',
                })}
              >
                {labels.soldOutText}
              </Badge>
            )}

            {option.description && (
              <StyledElement
                elementPath="checkout.variants.variantOptions.description"
                as="div"
                className={clsx(
                  'cp_variant-description',
                  'text-[var(--cp_checkout-variants-variantOptions-description-color)]',
                  '[font-family:var(--cp_checkout-variants-variantOptions-description-font-family)]',
                  '[font-style:var(--cp_checkout-variants-variantOptions-description-font-style)]',
                  '[font-size:var(--cp_checkout-variants-variantOptions-description-font-size)]',
                  '[line-height:var(--cp_checkout-variants-variantOptions-description-line-height)]',
                  '[font-weight:var(--cp_checkout-variants-variantOptions-description-font-weight)]',
                )}
              >
                <MultilineText dangerouslySetInnerHTML={{ __html: option.description }} />
              </StyledElement>
            )}

            {image && isPwyw && variant.selectionType !== 'quantity' && !isSoldOut && (
              <div className="cp_variant-option-pwyw-wrapper flex flex-col gap-y-2 mt-2">
                <VariantOptionPayWhatYouWantInput
                  variantId={variantId}
                  option={option}
                  currency={currency}
                  compact={variant.layout?.variantOptionLayout !== 'grid'}
                  isSelected={isSelected}
                  onSelect={onPwywSelect}
                  textAlign={textAlign}
                  variant={variant}
                />
              </div>
            )}
          </div>

          {variant.selectionType === 'quantity' && !isSoldOut && (
            <div
              className={clsx('cp_variant-option-quantity-wrapper flex flex-col gap-y-2', {
                'ml-auto items-end': variant.layout?.variantOptionLayout !== 'grid',
              })}
            >
              <VariantOptionQuantitySelect
                textAlign={variant.layout?.textAlign}
                option={option}
                selectedQuantity={quantity}
                setQuantity={setQuantity}
              />

              {isPwyw && (
                <VariantOptionPayWhatYouWantInput
                  variantId={variantId}
                  option={option}
                  currency={currency}
                  isSelected={isSelected}
                  onSelect={onPwywSelect}
                  textAlign={textAlign}
                  variant={variant}
                />
              )}

              {showVariantOptionTotal && (
                <div className="cp_variant-total [font-size:var(--cp_font-body-size-sm)] [line-height:var(--cp_font-body-line-height-sm)]">
                  {labels.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: variantOptionTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}

              {showPwywQuantityTotal && (
                <div className="cp_variant-total text-gray-700 text-sm">
                  {labels.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: pwywQuantityTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}
            </div>
          )}

          {!image && isPwyw && variant.selectionType !== 'quantity' && !isSoldOut && (
            <div
              className={clsx('cp_variant-option-pwyw-wrapper flex flex-col gap-y-2', {
                'ml-auto': variant.layout?.variantOptionLayout !== 'grid',
              })}
            >
              <VariantOptionPayWhatYouWantInput
                variantId={variantId}
                option={option}
                currency={currency}
                compact={variant.layout?.variantOptionLayout !== 'grid'}
                isSelected={isSelected}
                onSelect={onPwywSelect}
                textAlign={textAlign}
                variant={variant}
              />
            </div>
          )}
        </div>
      )}

      {textAlign === 'center' && (
        <div
          className={clsx('cp_variant-option-center flex flex-grow w-full gap-x-4', {
            'text-center flex-col': textAlign === 'center',
          })}
        >
          {image && (
            <div
              className={clsx(
                'cp_variant-image-wrapper rounded overflow-hidden flex-shrink-0 flex-grow-0 mb-2',
                {
                  'mx-auto': textAlign === 'center',
                  'w-16 h-16': imageSize === 'small',
                  'w-32 h-32': imageSize === 'medium',
                  'w-64 h-64': imageSize === 'large',
                },
              )}
            >
              <img
                src={image.src}
                srcSet={image.srcSet}
                className="cp_variant-image object-cover h-full w-full"
              />
            </div>
          )}

          {showName && (
            <StyledElement
              elementPath="checkout.variants.variantOptions.name"
              as="div"
              className={clsx(
                'cp_variant-name overflow-hidden flex-shrink-0',
                'text-[var(--cp_checkout-variants-variantOptions-name-color)]',
                '[font-family:var(--cp_checkout-variants-variantOptions-name-font-family)]',
                '[font-style:var(--cp_checkout-variants-variantOptions-name-font-style)]',
                '[font-size:var(--cp_checkout-variants-variantOptions-name-font-size)]',
                '[line-height:var(--cp_checkout-variants-variantOptions-name-line-height)]',
                '[font-weight:var(--cp_checkout-variants-variantOptions-name-font-weight)]',
              )}
            >
              <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
            </StyledElement>
          )}

          {!isPwyw && ((showPrice && variantOptionPrice) || isSoldOut) && (
            <div
              className={clsx('cp_variant-price-wrapper gap-x-1.5 flex', {
                'flex-1': !showName,
                'flex-col text-center justify-center': textAlign === 'center',
              })}
            >
              {showPrice && variantOptionPrice > 0 && showVariantOptionPrice && (
                <>
                  <StyledElement
                    elementPath="checkout.variants.variantOptions.price"
                    as="div"
                    className={clsx(
                      'cp_variant-price space-y-1 w-full',
                      'text-[var(--cp_checkout-variants-variantOptions-price-color)]',
                      '[font-family:var(--cp_checkout-variants-variantOptions-price-font-family)]',
                      '[font-style:var(--cp_checkout-variants-variantOptions-price-font-style)]',
                      '[font-size:var(--cp_checkout-variants-variantOptions-price-font-size)]',
                      '[line-height:var(--cp_checkout-variants-variantOptions-price-line-height)]',
                      '[font-weight:var(--cp_checkout-variants-variantOptions-price-font-weight)]',
                    )}
                  >
                    {discountedFromPrice && (
                      <StyledElement
                        elementPath="checkout.variants.variantOptions.discountedFromPrice"
                        as="span"
                        className={clsx(
                          'cp_variant-discounted-from line-through mr-1.5',
                          'text-[var(--cp_checkout-variants-variantOptions-discountedFromPrice-color)]',
                          '[font-family:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-family)]',
                          '[font-style:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-style)]',
                          '[font-size:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-size)]',
                          '[line-height:var(--cp_checkout-variants-variantOptions-discountedFromPrice-line-height)]',
                          '[font-weight:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-weight)]',
                        )}
                      >
                        {formatPrice({
                          amount: discountedFromPrice,
                          currency: currency,
                          locale: locale,
                          style: 'currency',
                        })}
                      </StyledElement>
                    )}

                    {showPriceSign && variantOptionPrice > 0 && (
                      <span className="cp_variant-price-plus-sign text-[var(--cp_checkout-variants-variantOptions-plusSign-color)]">
                        +
                      </span>
                    )}

                    <span className="cp_variant-price-amount">
                      {formatPrice({
                        amount: currentVariantOptionPrice || variantOptionPrice,
                        currency: currency,
                        locale: locale,
                        style: 'currency',
                      })}
                    </span>

                    <VariantOptionIntervalSuffix
                      option={option}
                      showInterval={showInterval}
                      effectiveInterval={variantEffectiveInterval}
                      labels={labels}
                    />
                  </StyledElement>

                  {shouldShowVariantPlanIterations({
                    selectedPrice,
                    product,
                    option,
                    planIterations,
                  }) && <PlanIterations planIterations={planIterations} />}
                </>
              )}

              {isSoldOut && (
                <Badge
                  className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses, {
                    'mr-auto': textAlign === 'left',
                  })}
                >
                  {labels.soldOutText}
                </Badge>
              )}
            </div>
          )}

          {isPwyw && isSoldOut && (
            <Badge className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses)}>
              {labels.soldOutText}
            </Badge>
          )}

          {option.description && (
            <StyledElement
              elementPath="checkout.variants.variantOptions.description"
              as="div"
              className={clsx(
                'cp_variant-description',
                'text-[var(--cp_checkout-variants-variantOptions-description-color)]',
                '[font-family:var(--cp_checkout-variants-variantOptions-description-font-family)]',
                '[font-style:var(--cp_checkout-variants-variantOptions-description-font-style)]',
                '[font-size:var(--cp_checkout-variants-variantOptions-description-font-size)]',
                '[line-height:var(--cp_checkout-variants-variantOptions-description-line-height)]',
                '[font-weight:var(--cp_checkout-variants-variantOptions-description-font-weight)]',
              )}
            >
              <MultilineText dangerouslySetInnerHTML={{ __html: option.description }} />
            </StyledElement>
          )}

          {variant.selectionType === 'quantity' && !isSoldOut && (
            <div
              className={clsx(
                'cp_variant-option-quantity-wrapper flex flex-col gap-y-2 mt-2 items-center',
              )}
            >
              <VariantOptionQuantitySelect
                textAlign={variant.layout?.textAlign}
                option={option}
                selectedQuantity={quantity}
                setQuantity={setQuantity}
              />

              {isPwyw && (
                <VariantOptionPayWhatYouWantInput
                  variantId={variantId}
                  option={option}
                  currency={currency}
                  isSelected={isSelected}
                  onSelect={onPwywSelect}
                  textAlign={textAlign}
                  variant={variant}
                />
              )}

              {showVariantOptionTotal && (
                <div className="cp_variant-total text-center [font-size:var(--cp_font-body-size-sm)] [line-height:var(--cp_font-body-line-height-sm)]">
                  {labels.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: variantOptionTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}

              {showPwywQuantityTotal && (
                <div className="cp_variant-total text-center text-gray-700 text-sm">
                  {labels.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: pwywQuantityTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}
            </div>
          )}

          {isPwyw && variant.selectionType !== 'quantity' && !isSoldOut && (
            <div className="cp_variant-option-pwyw-wrapper flex flex-col gap-y-2 mt-2">
              <VariantOptionPayWhatYouWantInput
                variantId={variantId}
                option={option}
                currency={currency}
                isSelected={isSelected}
                onSelect={onPwywSelect}
                textAlign={textAlign}
                variant={variant}
              />
            </div>
          )}
        </div>
      )}

      {textAlign === 'stack' && (
        <div
          className={clsx('cp_variant-option-stack w-full', {
            'text-left': textAlign === 'stack',
          })}
        >
          {image && (
            <div
              className={clsx(
                'cp_variant-image-wrapper rounded overflow-hidden flex-shrink-0 flex-grow-0 mb-2',
                {
                  'w-16 h-16': imageSize === 'small',
                  'w-32 h-32': imageSize === 'medium',
                  'w-64 h-64': imageSize === 'large',
                },
              )}
            >
              <img
                src={image.src}
                srcSet={image.srcSet}
                className="cp_variant-image object-cover h-full w-full"
              />
            </div>
          )}

          {showName && (
            <StyledElement
              elementPath="checkout.variants.variantOptions.name"
              as="div"
              className={clsx(
                'cp_variant-name overflow-hidden flex-shrink-0',
                'text-[var(--cp_checkout-variants-variantOptions-name-color)]',
                '[font-family:var(--cp_checkout-variants-variantOptions-name-font-family)]',
                '[font-style:var(--cp_checkout-variants-variantOptions-name-font-style)]',
                '[font-size:var(--cp_checkout-variants-variantOptions-name-font-size)]',
                '[line-height:var(--cp_checkout-variants-variantOptions-name-line-height)]',
                '[font-weight:var(--cp_checkout-variants-variantOptions-name-font-weight)]',
              )}
            >
              <MultilineText dangerouslySetInnerHTML={{ __html: option.name }} />
            </StyledElement>
          )}

          {!isPwyw && ((showPrice && variantOptionPrice) || isSoldOut) && (
            <div
              className={clsx(
                'cp_variant-price-wrapper gap-x-1.5 inline-flex flex-col items-start',
                {
                  'flex-1': !showName,
                },
              )}
            >
              {showPrice && variantOptionPrice > 0 && showVariantOptionPrice && (
                <>
                  <StyledElement
                    elementPath="checkout.variants.variantOptions.price"
                    as="div"
                    className={clsx(
                      'cp_variant-price space-y-1 w-full',
                      'text-[var(--cp_checkout-variants-variantOptions-price-color)]',
                      '[font-family:var(--cp_checkout-variants-variantOptions-price-font-family)]',
                      '[font-style:var(--cp_checkout-variants-variantOptions-price-font-style)]',
                      '[font-size:var(--cp_checkout-variants-variantOptions-price-font-size)]',
                      '[line-height:var(--cp_checkout-variants-variantOptions-price-line-height)]',
                      '[font-weight:var(--cp_checkout-variants-variantOptions-price-font-weight)]',
                    )}
                  >
                    {discountedFromPrice && (
                      <StyledElement
                        elementPath="checkout.variants.variantOptions.discountedFromPrice"
                        as="span"
                        className={clsx(
                          'cp_variant-discounted-from line-through mr-1.5',
                          'text-[var(--cp_checkout-variants-variantOptions-discountedFromPrice-color)]',
                          '[font-family:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-family)]',
                          '[font-style:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-style)]',
                          '[font-size:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-size)]',
                          '[line-height:var(--cp_checkout-variants-variantOptions-discountedFromPrice-line-height)]',
                          '[font-weight:var(--cp_checkout-variants-variantOptions-discountedFromPrice-font-weight)]',
                        )}
                      >
                        {formatPrice({
                          amount: discountedFromPrice,
                          currency: currency,
                          locale: locale,
                          style: 'currency',
                        })}
                      </StyledElement>
                    )}

                    {showPriceSign && option.additionalChargeAmount > 0 && (
                      <span className="cp_variant-price-plus-sign text-[var(--cp_checkout-variants-variantOptions-plusSign-color)]">
                        +
                      </span>
                    )}

                    <span className="cp_variant-price-amount">
                      {formatPrice({
                        amount: variantOptionPrice,
                        currency: currency,
                        locale: locale,
                        style: 'currency',
                      })}
                    </span>

                    <VariantOptionIntervalSuffix
                      option={option}
                      showInterval={showInterval}
                      effectiveInterval={variantEffectiveInterval}
                      labels={labels}
                    />
                  </StyledElement>

                  {shouldShowVariantPlanIterations({
                    selectedPrice,
                    product,
                    option,
                    planIterations,
                  }) && <PlanIterations planIterations={planIterations} />}
                </>
              )}

              {isSoldOut && (
                <Badge
                  className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses, {
                    'mr-auto': textAlign === 'left',
                  })}
                >
                  {labels.soldOutText}
                </Badge>
              )}
            </div>
          )}

          {isPwyw && isSoldOut && (
            <Badge
              className={clsx('cp_sold-out cp_variant-soldout', variantSoldOutClasses, 'mr-auto')}
            >
              {labels.soldOutText}
            </Badge>
          )}

          {option.description && (
            <StyledElement
              elementPath="checkout.variants.variantOptions.description"
              as="div"
              className={clsx(
                'cp_variant-description',
                'text-[var(--cp_checkout-variants-variantOptions-description-color)]',
                '[font-family:var(--cp_checkout-variants-variantOptions-description-font-family)]',
                '[font-style:var(--cp_checkout-variants-variantOptions-description-font-style)]',
                '[font-size:var(--cp_checkout-variants-variantOptions-description-font-size)]',
                '[line-height:var(--cp_checkout-variants-variantOptions-description-line-height)]',
                '[font-weight:var(--cp_checkout-variants-variantOptions-description-font-weight)]',
              )}
            >
              <MultilineText dangerouslySetInnerHTML={{ __html: option.description }} />
            </StyledElement>
          )}

          {variant.selectionType === 'quantity' && !isSoldOut && (
            <div
              className={clsx(
                'cp_variant-option-quantity-wrapper flex flex-col gap-y-2 mt-2 items-start',
              )}
            >
              <VariantOptionQuantitySelect
                textAlign={variant.layout?.textAlign}
                option={option}
                selectedQuantity={quantity}
                setQuantity={setQuantity}
              />

              {isPwyw && (
                <VariantOptionPayWhatYouWantInput
                  variantId={variantId}
                  option={option}
                  currency={currency}
                  isSelected={isSelected}
                  onSelect={onPwywSelect}
                  textAlign={textAlign}
                  variant={variant}
                />
              )}

              {showVariantOptionTotal && (
                <div className="cp_variant-total [font-size:var(--cp_font-body-size-sm)] [line-height:var(--cp_font-body-line-height-sm)]">
                  {labels.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: variantOptionTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}

              {showPwywQuantityTotal && (
                <div className="cp_variant-total text-gray-700 text-sm">
                  {labels.variantOptionTotalText}{' '}
                  {formatPrice({
                    amount: pwywQuantityTotal,
                    currency: currency,
                    locale: locale,
                    style: 'currency',
                  })}
                </div>
              )}
            </div>
          )}

          {isPwyw && variant.selectionType !== 'quantity' && !isSoldOut && (
            <div className="cp_variant-option-pwyw-wrapper flex flex-col gap-y-2 mt-2">
              <VariantOptionPayWhatYouWantInput
                variantId={variantId}
                option={option}
                currency={currency}
                isSelected={isSelected}
                onSelect={onPwywSelect}
                textAlign={textAlign}
                variant={variant}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanIterations({ planIterations }) {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);
  return (
    <StyledElement
      elementPath="checkout.variants.variantOptions.planIterations"
      as="div"
      className={clsx(
        'cp_variant-price-ends-after-text',
        'text-[var(--cp_checkout-variants-variantOptions-planIterations-color)]',
        '[font-family:var(--cp_checkout-variants-variantOptions-planIterations-font-family)]',
        '[font-style:var(--cp_checkout-variants-variantOptions-planIterations-font-style)]',
        '[font-size:var(--cp_checkout-variants-variantOptions-planIterations-font-size)]',
        '[line-height:var(--cp_checkout-variants-variantOptions-planIterations-line-height)]',
        '[font-weight:var(--cp_checkout-variants-variantOptions-planIterations-font-weight)]',
      )}
    >
      <span className="cp_variant-price-ends-after-text">{labels.endsAfterText}</span>{' '}
      <span className="cp_variant-price-plan-iterations">{planIterations}</span>{' '}
      <span className="cp_variant-price-ends-after-payments-text">
        {labels.endsAfterPaymentsText}
      </span>
    </StyledElement>
  );
}

function VariantOptionPayWhatYouWantInput({
  variantId,
  option,
  currency,
  compact = false,
  isSelected,
  onSelect,
  textAlign = 'left',
  variant,
}) {
  const { setVariantOptionPayWhatYouWantAmount } = useCheckoutFormHelpers();
  const { form, checkout, theme, product } = useCheckoutState();
  const selectedPrice = useSelectedPrice();
  const labels = getThemeLabels(theme);
  const node = useRef();
  const suffixRef = useRef(null);

  // Interval / Currency suffix — interval for a recurring option (following the
  // selected price's cadence), else the currency code (like the picker PWYW).
  const effectiveInterval = getEffectiveInterval(selectedPrice, product);
  const showInterval = shouldShowVariantOptionInterval({ product, option, effectiveInterval });
  const suffixText = getVariantPwywSuffix({
    option,
    showInterval,
    effectiveInterval,
    currency,
    labels,
  });
  const suffixEnabled = resolveInheritedEnabled(
    theme?.elements,
    'checkout.variants.variantOptions.pwywSuffix',
    'checkout.pwyw.suffix',
  );
  const showSuffix = !!suffixText && suffixEnabled;

  const [suffixWidth, setSuffixWidth] = useState(null);
  useIsomorphicLayoutEffect(() => {
    setSuffixWidth(showSuffix && suffixRef.current ? suffixRef.current.offsetWidth : null);
  }, [showSuffix, suffixText]);

  const formOption = form.variants[variantId]?.find(v => v.optionId === option._id);
  const minimumPrice = option.additionalChargeAmount || 0;

  const displayValue =
    formOption?.payWhatYouWantAmount != null ? String(formOption.payWhatYouWantAmount) : '';

  const showPlaceholder = variant?.layout?.showPwywPlaceholder ?? true;
  const placeholder = !showPlaceholder
    ? ''
    : option.pwywSuggestedPrice > 0
      ? `${labels.pwywSuggestedPricePlaceholderText} ${formatPrice({ amount: option.pwywSuggestedPrice, currency, locale: checkout.locale, style: 'currency' })}`
      : minimumPrice > 0
        ? `${labels.pwywMinPricePlaceholderText} ${formatPrice({ amount: minimumPrice, currency, locale: checkout.locale, style: 'currency' })}`
        : labels.pwywEnterAmountText;

  useEffect(() => {
    const handleScroll = () => {
      node.current?.blur();
    };

    const currentNode = node.current;
    if (currentNode) {
      currentNode.addEventListener('wheel', handleScroll, { passive: true });
    }

    return () => {
      if (currentNode) {
        currentNode.removeEventListener('wheel', handleScroll);
      }
    };
  }, []);

  return (
    <div
      className="cp_variant-pwyw-input relative flex-shrink-0"
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <TextInput
        innerRef={node}
        className={clsx('cp_form-textinput text-left', {
          '!w-30': compact,
          'w-full': textAlign === 'stack' || textAlign === 'center',
          'max-w-30': textAlign === 'left',
        })}
        style={
          showSuffix
            ? {
                paddingRight: `calc(var(--cp_checkout-variants-variantOptions-pwywInput-padding-x) + ${
                  suffixWidth ?? 30
                }px + 0.4em)`,
              }
            : undefined
        }
        id={`pwyw-${variantId}-${option._id}`}
        name={`pwyw-${variantId}-${option._id}`}
        type="number"
        step="any"
        min="0"
        placeholder={placeholder}
        value={displayValue}
        onKeyDown={e => {
          if (e.key === 'e' || e.key === 'E' || e.key === '-') {
            e.preventDefault();
          }
        }}
        onFocus={() => {
          if (!isSelected) {
            onSelect(option._id);
          }
        }}
        onChange={e => {
          const rawValue = String(e.target.value || '');
          const value = rawValue
            .replace(/[^\d.,]+/g, '')
            .replace(/(\.\d{2})\d+|(\,\d{2})\d+/, '$1$2')
            .replace(/(\d+)([.,])(\d{2}).*/, '$1$2$3');

          // Store the display value
          setVariantOptionPayWhatYouWantAmount(variantId, option._id, value);
        }}
      />
      {showSuffix && (
        <span
          ref={suffixRef}
          className="cp_paywhatyouwant-currency pointer-events-none absolute top-1/2 -translate-y-1/2"
        >
          {suffixText}
        </span>
      )}
    </div>
  );
}

function getImageSrcAndSrcSetHelper({ url, imageSize = 'small', isPreview = false }) {
  if (url == null) return;

  const { height, width } = (() => {
    switch (imageSize) {
      case 'small':
        return { height: 64, width: 64 };
      case 'medium':
        return { height: 128, width: 128 };
      case 'large':
      default:
        return { height: 256, width: 256 };
    }
  })();

  return getImageSrcAndSrcSet({ url, width, height, isPreview });
}

export default Variants;
